/**
 * HOOK: useDynamicBranding
 * 
 * Inyecta variables CSS dinámicamente basadas en el branding de la app.
 * Lee el parámetro `app` de la URL y aplica las variables CSS correspondientes.
 * 
 * Si `app` es desconocido o null, aplica el branding por defecto de "Accounts Central".
 * 
 * @package @accounts/hooks
 */

import { useEffect } from 'react';
import { getBrandingConfig, BRANDING_CONFIGS } from '@shared/config/brandingConfig';

/**
 * Hook para aplicar branding dinámico basado en el parámetro `app` de la URL
 * 
 * @param appName - Nombre de la app desde query params (ej: 'empresa', 'tienda', 'houseme')
 * 
 * @example
 * ```tsx
 * const LoginPage = () => {
 *   const [params] = useSearchParams();
 *   const app = params.get('app');
 *   
 *   useDynamicBranding(app); // Aplica branding automáticamente
 *   
 *   return <div>...</div>;
 * };
 * ```
 */
export function useDynamicBranding(appName: string | null): void {
  useEffect(() => {
    // Obtener configuración de branding (fallback a 'default' si appName es null o desconocido)
    const config = getBrandingConfig(appName);
    
    // Aplicar branding
    applyBranding(config);
    
    // Cleanup: restaurar valores por defecto al desmontar o cambiar app
    return () => {
      restoreDefaultBranding();
    };
  }, [appName]);
}

/**
 * Aplica las variables CSS de branding al documento
 * 
 * @param config - Configuración de branding a aplicar
 */
function applyBranding(config: typeof BRANDING_CONFIGS.default): void {
  const root = document.documentElement;
  
  // Inyectar todas las variables CSS de colores
  Object.entries(config.colors).forEach(([key, value]) => {
    // Convertir camelCase a kebab-case para variables CSS
    // Ej: primaryForeground -> --primary-foreground
    const cssVarName = `--${key.replace(/([A-Z])/g, '-$1').toLowerCase()}`;
    root.style.setProperty(cssVarName, value);
  });
  
  // 🎨 INYECTAR VARIABLES DE ESTRUCTURA (ADN DE DISEÑO)
  if (config.design) {
    // Border radius
    root.style.setProperty('--radius', config.design.radius);
    
    // Font family
    root.style.setProperty('--font-main', config.design.fontFamily);
    
    // Sombras
    root.style.setProperty('--shadow-sm', config.design.shadows.sm);
    root.style.setProperty('--shadow-md', config.design.shadows.md);
    root.style.setProperty('--shadow-lg', config.design.shadows.lg);
    
    // Aplicar font-family directamente al documento
    root.style.fontFamily = config.design.fontFamily;
    
    // Cargar Google Fonts dinámicamente si están configuradas
    if (config.design.googleFonts) {
      loadGoogleFonts(config.design.googleFonts);
    }
  }
  
  // Actualizar favicon si existe
  if (config.assets.favicon) {
    updateFavicon(config.assets.favicon);
  }
}

/**
 * Restaura las variables CSS a sus valores por defecto
 * 
 * Las variables CSS definidas en :root en index.scss se restauran automáticamente
 * al remover las propiedades inline, pero las removemos explícitamente para limpieza.
 */
function restoreDefaultBranding(): void {
  const root = document.documentElement;
  const defaultConfig = BRANDING_CONFIGS.default;
  
  // Remover todas las variables CSS inyectadas (colores)
  Object.keys(defaultConfig.colors).forEach((key) => {
    const cssVarName = `--${key.replace(/([A-Z])/g, '-$1').toLowerCase()}`;
    root.style.removeProperty(cssVarName);
  });
  
  // Restaurar variables de estructura a valores por defecto
  if (defaultConfig.design) {
    root.style.setProperty('--radius', defaultConfig.design.radius);
    root.style.setProperty('--font-main', defaultConfig.design.fontFamily);
    root.style.setProperty('--shadow-sm', defaultConfig.design.shadows.sm);
    root.style.setProperty('--shadow-md', defaultConfig.design.shadows.md);
    root.style.setProperty('--shadow-lg', defaultConfig.design.shadows.lg);
    root.style.fontFamily = defaultConfig.design.fontFamily;
  }
}

/**
 * Actualiza el favicon del documento
 * 
 * @param faviconPath - Ruta al favicon
 */
function updateFavicon(faviconPath: string): void {
  // Buscar link existente
  let link = document.querySelector("link[rel~='icon']") as HTMLLinkElement;
  
  if (link) {
    // Actualizar href existente
    link.href = faviconPath;
  } else {
    // Crear nuevo link si no existe
    link = document.createElement('link');
    link.rel = 'icon';
    link.href = faviconPath;
    document.head.appendChild(link);
  }
}

/**
 * Carga Google Fonts dinámicamente
 * 
 * @param googleFonts - Configuración de Google Fonts
 */
function loadGoogleFonts(googleFonts: { families: string[]; display?: string }): void {
  // Verificar si ya existe un link de Google Fonts para evitar duplicados
  const existingLink = document.querySelector('link[href*="fonts.googleapis.com"]') as HTMLLinkElement;
  
  if (existingLink) {
    // Actualizar href existente
    const familiesParam = googleFonts.families.join('&family=');
    const displayParam = googleFonts.display || 'swap';
    existingLink.href = `https://fonts.googleapis.com/css2?family=${familiesParam}&display=${displayParam}`;
    return;
  }
  
  // Crear preconnect links para mejor rendimiento
  const preconnect1 = document.createElement('link');
  preconnect1.rel = 'preconnect';
  preconnect1.href = 'https://fonts.googleapis.com';
  document.head.appendChild(preconnect1);
  
  const preconnect2 = document.createElement('link');
  preconnect2.rel = 'preconnect';
  preconnect2.href = 'https://fonts.gstatic.com';
  preconnect2.crossOrigin = 'anonymous';
  document.head.appendChild(preconnect2);
  
  // Crear link de Google Fonts
  const link = document.createElement('link');
  const familiesParam = googleFonts.families.join('&family=');
  const displayParam = googleFonts.display || 'swap';
  link.rel = 'stylesheet';
  link.href = `https://fonts.googleapis.com/css2?family=${familiesParam}&display=${displayParam}`;
  document.head.appendChild(link);
}
